'use client'

import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '@/utilities/ui'
import type { CommitmentTerm } from '../cells/types'

interface HeaderCommitmentDropdownProps {
  value: CommitmentTerm
  onValueChange: (value: CommitmentTerm) => void
}

const termOptions: { value: CommitmentTerm; label: string }[] = [
  { value: '6', label: '6 months' },
  { value: '12', label: '12 months' },
  { value: '24', label: '24 months' },
  { value: '36', label: '36 months' },
]

/**
 * Custom commitment dropdown styled to look like header text.
 * Uses relative positioning to avoid portal issues with scrollable containers.
 */
export const HeaderCommitmentDropdown: React.FC<HeaderCommitmentDropdownProps> = ({
  value,
  onValueChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const currentLabel = termOptions.find((opt) => opt.value === value)?.label || '36 months'

  const handleSelect = (newValue: CommitmentTerm) => {
    onValueChange(newValue)
    setIsOpen(false)
  }

  return (
    <div ref={dropdownRef} className="relative mb-5">
      <span className="text-white table-header">Commitment</span>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="absolute left-0 top-[calc(100%+4px)] flex items-center gap-1 text-white text-xs font-normal leading-tight hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded whitespace-nowrap"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Commitment term: ${currentLabel}. Click to change.`}
      >
        {currentLabel}
        <ChevronDown
          className={cn('h-3 w-3 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-[calc(100%+24px)] z-50 min-w-[140px] bg-civo-black rounded-lg border border-white/30 shadow-lg py-1"
          role="listbox"
          aria-label="Select commitment term"
        >
          {termOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={value === option.value}
              onClick={() => handleSelect(option.value)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-left body-xsmall text-white hover:bg-white/10 focus:bg-white/10 focus:outline-none',
              )}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                {value === option.value && <Check className="h-4 w-4" />}
              </span>
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
